import React, { useState } from "react";

/** One entity considered by a generation run. Mirrors the backend report entry. */
export type GenerationReportEntity = {
  target_schema: string | null;
  target_table: string | null;
  construct: string | null;
  status: "generated" | "skipped" | "failed" | string;
  reason_code?: string | null;
  message?: string | null;
};

export type GenerationReport = {
  expected: number;
  generated: number;
  skipped: number;
  failed: number;
  entities: GenerationReportEntity[];
};

const statusStyles: Record<string, string> = {
  skipped: "tw-bg-amber-50 tw-text-amber-700 tw-border-amber-200",
  failed: "tw-bg-rose-50 tw-text-rose-700 tw-border-rose-200",
  generated: "tw-bg-emerald-50 tw-text-emerald-700 tw-border-emerald-200"
};

const describe = (entity: GenerationReportEntity): string => {
  const name = [entity.target_schema, entity.target_table].filter(Boolean).join(".");
  return name || entity.target_table || "(unnamed entity)";
};


/**
 * Coverage of a generation run: "33/34 tables generated", plus the entities that did
 * not make it and why. The generator has always skipped entities silently (they were
 * only visible in the server log), which made a partial result look complete.
 */
export function GenerationReportSummary({
  report
}: {
  report: GenerationReport | null;
}): JSX.Element | null {
  const [expanded, setExpanded] = useState(false);

  if (!report || report.expected === 0) {
    return null;
  }

  // FAILURES FIRST. The list is capped in height and scrolls, so with a handful of skips the
  // failures sat below the fold — a run reading "2 failed — 2 skipped" showed only the two skips,
  // and the reader concluded nothing had failed. A failure is the thing to act on; a skip is
  // usually deliberate ("generate model entity" is off).
  const statusRank: Record<string, number> = { failed: 0, skipped: 1 };
  const problems = report.entities
    .filter((entity) => entity.status !== "generated")
    .slice()
    .sort(
      (left, right) =>
        (statusRank[left.status] ?? 2) - (statusRank[right.status] ?? 2) ||
        `${left.target_schema}.${left.target_table}`.localeCompare(
          `${right.target_schema}.${right.target_table}`
        )
    );
  const isComplete = problems.length === 0;
  const tone = report.failed
    ? "tw-border-rose-200 tw-bg-rose-50 tw-text-rose-800"
    : problems.length
      ? "tw-border-amber-200 tw-bg-amber-50 tw-text-amber-800"
      : "tw-border-emerald-200 tw-bg-emerald-50 tw-text-emerald-800";

  return (
    <div className={`tw-mt-3 tw-rounded-2xl tw-border tw-px-3 tw-py-2 ${tone}`}>
      <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
        <p className="tw-text-sm tw-font-semibold">
          {report.generated}/{report.expected} tables generated
          {report.failed ? ` — ${report.failed} failed` : ""}
          {report.skipped ? ` — ${report.skipped} skipped` : ""}
        </p>
        {!isComplete ? (
          <button
            type="button"
            onClick={() => setExpanded((previous) => !previous)}
            className="tw-rounded-full tw-border tw-border-current tw-px-3 tw-py-1 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide"
          >
            {expanded ? "Hide details" : "Show details"}
          </button>
        ) : null}
      </div>
      {expanded && !isComplete ? (
        <ul className="tw-mt-2 tw-flex tw-max-h-80 tw-flex-col tw-gap-1.5 tw-overflow-auto">
          {problems.map((entity, index) => (
            <li
              key={`${entity.target_schema}.${entity.target_table}.${index}`}
              className={`tw-rounded-xl tw-border tw-px-2.5 tw-py-1.5 ${
                statusStyles[entity.status] ?? statusStyles.skipped
              }`}
            >
              <p className="tw-text-xs tw-font-semibold">
                {describe(entity)}
                {entity.construct ? (
                  <span className="tw-font-normal"> · {entity.construct}</span>
                ) : null}
                <span className="tw-font-normal"> · {entity.status}</span>
              </p>
              {entity.message ? (
                <p className="tw-text-[11px] tw-font-medium">{entity.message}</p>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
